// Recovery Library — full topic catalog.
// Categories and article placeholders. Content is added/edited over time by
// the Recovery Trauma™ team. Each article resolves at /library/$slug; if no
// full article exists yet (in library-resources.ts or guides.ts), a gentle
// "coming soon" placeholder is rendered.

export type CatalogArticle = {
  slug: string;
  title: string;
};

export type CatalogCategory = {
  id: string;
  label: string;
  emoji: string;
  blurb: string;
  articles: CatalogArticle[];
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const cat = (
  id: string,
  label: string,
  emoji: string,
  blurb: string,
  titles: string[],
): CatalogCategory => ({
  id,
  label,
  emoji,
  blurb,
  articles: titles.map((t) => ({ slug: slugify(t), title: t })),
});

export const LIBRARY_CATEGORIES: CatalogCategory[] = [
  cat(
    "trauma-recovery",
    "Trauma Recovery",
    "📚",
    "Foundational guides for understanding and healing trauma.",
    [
      "Understanding Trauma",
      "Understanding CPTSD",
      "Understanding PTSD",
      "Emotional Flashbacks",
      "Hypervigilance",
      "Working with Triggers",
      "Healing from Trauma",
      "Phases of Trauma Recovery",
      "Window of Tolerance",
      "Safety and Stabilisation",
    ],
  ),
  cat(
    "nervous-system",
    "Nervous System Healing",
    "🌿",
    "Polyvagal-informed tools for a steadier body.",
    [
      "Polyvagal Theory Basics",
      "Nervous System Regulation",
      "Fight, Flight, Freeze and Fawn",
      "Co-Regulation",
      "Self-Regulation Practices",
      "Somatic Practices",
      "Grounding Techniques",
      "Understanding Stress Responses",
      "Building Resilience",
    ],
  ),
  cat(
    "trauma-bonds",
    "Trauma Bonds & Narcissistic Abuse",
    "💔",
    "Understanding, leaving, and healing from coercive bonds.",
    [
      "What Is a Trauma Bond?",
      "Signs of Trauma Bonding",
      "Breaking a Trauma Bond",
      "Narcissistic Abuse Recovery",
      "Gaslighting",
      "Love Bombing",
      "Trauma Bond Withdrawal",
      "No Contact and Healing",
      "Rebuilding Trust After Abuse",
    ],
  ),
  cat(
    "attachment",
    "Attachment & Relationships",
    "🤝",
    "Attachment styles, repair, and emotional safety.",
    [
      "Attachment Styles",
      "Secure Attachment",
      "Anxious Attachment",
      "Avoidant Attachment",
      "Disorganised Attachment",
      "Relationship Triggers",
      "Healthy Relationships",
      "Repair After Rupture",
      "Communicating Needs",
      "Building Emotional Safety",
    ],
  ),
  cat(
    "self-worth",
    "Self-Worth & Identity",
    "💛",
    "Rebuilding a kind, steady relationship with yourself.",
    [
      "Rebuilding Self-Worth",
      "Healing Shame",
      "Inner Critic Work",
      "Self-Compassion",
      "Confidence After Trauma",
      "Identity After Trauma",
      "Perfectionism",
      "People Pleasing",
      "Self-Acceptance",
    ],
  ),
  cat(
    "inner-child",
    "Inner Child Healing",
    "🌱",
    "Reparenting and tending the younger you.",
    [
      "Meeting Your Inner Child",
      "Reparenting Basics",
      "Childhood Emotional Neglect",
      "Healing Childhood Trauma",
      "Letters to Younger You",
      "Understanding Core Wounds",
      "Abandonment Wounds",
      "Rejection Wounds",
    ],
  ),
  cat(
    "anxiety",
    "Anxiety & Emotional Wellbeing",
    "🌊",
    "Calming the wave when it rises.",
    [
      "Understanding Anxiety",
      "Calming Anxiety",
      "Panic Attacks",
      "Racing Thoughts",
      "Managing Worry",
      "Sleep and Anxiety",
      "Emotional Regulation",
      "Sitting with Feelings",
      "Mindfulness for Anxiety",
    ],
  ),
  cat(
    "grief",
    "Grief & Loss",
    "🕊️",
    "Tending grief with care and meaning.",
    [
      "Understanding Grief",
      "Stages of Grief",
      "Complicated Grief",
      "Loss and Trauma",
      "Self-Care During Grief",
      "Coping with Anniversaries",
      "Meaning After Loss",
    ],
  ),
  cat(
    "therapy-approaches",
    "Therapy Approaches",
    "🧠",
    "Evidence-based modalities used in trauma recovery.",
    [
      "Cognitive Behavioural Therapy (CBT)",
      "Dialectical Behaviour Therapy (DBT)",
      "Internal Family Systems (IFS)",
      "EMDR",

      "Art Therapy",
      "Music Therapy",
      "Mindfulness-Based Approaches",
      "Acceptance and Commitment Therapy (ACT)",
    ],
  ),
  cat(
    "mindfulness",
    "Mindfulness & Wellbeing",
    "✨",
    "Daily practices for presence and self-care.",
    [
      "Mindfulness Basics",
      "Breathwork",
      "Meditation",
      "Nature Connection",
      "Forest Bathing",
      "Journaling",
      "Creative Expression",
      "Gratitude Practices",
      "Self-Care",
    ],
  ),
];

export function getCatalogArticle(
  slug: string,
): { article: CatalogArticle; category: CatalogCategory } | undefined {
  for (const c of LIBRARY_CATEGORIES) {
    const a = c.articles.find((x) => x.slug === slug);
    if (a) return { article: a, category: c };
  }
  return undefined;
}

/**
 * Somatic content used to appear three times in the library: the "Somatic
 * Practices" guide, a "Somatic Healing" article and a "Somatic Therapy"
 * article. "Somatic Practices" is now the single source of truth; the retired
 * slugs redirect to it so old links and search results still resolve.
 */
export const LIBRARY_SLUG_ALIASES: Record<string, string> = {
  "somatic-healing": "somatic-practices",
  "somatic-therapy": "somatic-practices",
};

/** Returns the canonical slug for a library entry (identity when not aliased). */
export function resolveLibrarySlug(slug: string): string {
  return LIBRARY_SLUG_ALIASES[slug] ?? slug;
}
